import { LearningPath, Concept } from '@/types/learning';

// 知识点概念库
export const CONCEPTS: Record<string, Concept> = {
  'prompt-engineering': {
    id: 'prompt-engineering',
    title: 'Prompt Engineering（提示词工程）',
    description: '学习如何编写高质量的AI提示词，获得更好的回复',
    content: `
Prompt Engineering是与AI模型交互的核心技能。好的提示词能够：
- 明确表达你的需求和期望
- 提供必要的上下文信息
- 引导AI给出结构化的回复
- 避免歧义和误解

**基本原则：**
1. 清晰明确：说明你想要什么
2. 提供上下文：给出背景信息
3. 指定格式：要求特定的输出结构
4. 分步引导：复杂任务分解成小步骤
5. 迭代优化：根据结果调整提示词
    `.trim(),
    examples: [
      '❌ 差: "写个代码"\n✅ 好: "用Python编写一个函数，接收用户列表，返回年龄大于18岁的用户，包含错误处理"',
      '❌ 差: "帮我调试"\n✅ 好: "这段代码报错TypeError: cannot read property \'name\' of undefined，请分析原因并提供修复方案"'
    ],
    relatedConcepts: ['context-window', 'few-shot-learning']
  },
  'context-window': {
    id: 'context-window',
    title: 'Context Window（上下文窗口）',
    description: 'AI模型能"记住"的对话长度限制',
    content: `
Context Window是AI模型一次能处理的文本量上限，以token为单位。

**不同模型的上下文窗口：**
- Claude Sonnet、Oups: 1M
- GLM-5: 1M
- Claude Mini: 1M tokens

**实际影响：**
- 超过窗口大小的内容会被"遗忘"
- 长对话需要总结历史内容
- 大型项目需要分段处理

**最佳实践：**
1. 重要信息放在提示词开头和结尾
2. 定期总结对话内容
3. 使用项目知识库功能
    `.trim(),
    relatedConcepts: ['prompt-engineering', 'token-usage']
  },
  'token-usage': {
    id: 'token-usage',
    title: 'Token使用与计费',
    description: '理解AI服务的计费单位和优化方法',
    content: `
Token是AI模型的基本处理单位，1 token ≈ 0.75个英文单词或0.5个中文字。

**计费规则：**
- 输入token（你的提示词）+ 输出token（AI的回复）
- 不同模型价格不同
- 上下文越长，消耗token越多

**优化技巧：**
1. 精简提示词，去除冗余信息
2. 使用简洁的自然语言
3. 避免重复发送相同内容
4. 合理使用缓存和历史记录
5. 选择适合任务的模型（简单任务用Mini，复杂任务用Max）

**示例消耗：**
- 简单代码问题：~500-1000 tokens（约$0.01）
- 中型项目重构：~5000-10000 tokens（约$0.10）
- 大型代码审查：~20000+ tokens（约$0.50+）
    `.trim(),
    relatedConcepts: ['context-window', 'model-selection']
  },
  'model-selection': {
    id: 'model-selection',
    title: '模型选择策略',
    description: '根据任务选择最合适的AI模型',
    content: `
不同的AI模型有不同的特点和适用场景。

**Claude系列：**
- Mini/Pro：日常编程、代码解释、简单调试
- Max：复杂重构、架构设计、深度代码审查
- Max Plus/Opus：企业级项目、多文件批量处理

**GLM-5系列：**
- 国产高性价比方案
- 专注中文编程场景
- 轻量级快速响应

**选择建议：**
1. 学习阶段：Mini（性价比高）
2. 日常开发：Pro（足够强大）
3. 复杂项目：Max（质量保证）
4. 企业应用：Max Plus（稳定可靠）
    `.trim(),
    relatedConcepts: ['token-usage', 'prompt-engineering']
  },
  'code-context': {
    id: 'code-context',
    title: '代码上下文管理',
    description: '如何让AI更好地理解你的项目',
    content: `
提供充分的代码上下文是获得准确答案的关键。

**应该提供的信息：**
1. 技术栈和框架版本
2. 相关文件的代码片段
3. 错误信息和堆栈跟踪
4. 预期行为和实际行为
5. 已尝试的解决方案

**上下文组织技巧：**
\`\`\`
项目背景：
- 框架：React 18 + TypeScript
- 状态管理：Redux Toolkit

问题描述：
用户登录后，状态更新但界面不刷新

相关代码：
[粘贴核心代码]

错误信息：
[粘贴完整报错]

已尝试：
1. 检查Redux DevTools，状态确实更新了
2. 添加console.log，组件没有重新渲染
\`\`\`

**避免的错误：**
- ❌ 只说"不工作"
- ❌ 提供完整项目代码（太长）
- ❌ 缺少关键配置信息
    `.trim(),
    relatedConcepts: ['prompt-engineering', 'debugging-with-ai']
  },
  'debugging-with-ai': {
    id: 'debugging-with-ai',
    title: 'AI辅助调试技巧',
    description: '高效利用AI解决代码bug',
    content: `
AI是强大的调试助手，但需要正确的使用方法。

**调试流程：**
1. 复现问题：说明如何触发bug
2. 收集信息：错误消息、日志、状态
3. 缩小范围：定位到具体函数或模块
4. 询问AI：提供精准的问题描述
5. 验证方案：测试AI的建议

**有效的调试提示词模板：**
\`\`\`
问题：[简明描述]
环境：[语言、框架、版本]
代码：
[最小可复现代码]
错误：
[完整错误信息]
预期：[应该发生什么]
实际：[实际发生什么]
\`\`\`

**高级技巧：**
1. 要求AI解释错误原因
2. 请求多个可能的解决方案
3. 询问为什么会发生这个问题
4. 要求提供测试用例
5. 学习如何预防类似问题

**示例对话：**
用户："这个函数报错了"
❌ 太模糊

用户："这个JavaScript函数在处理空数组时报TypeError，请分析原因并提供防御性编程方案"
✅ 清晰具体
    `.trim(),
    relatedConcepts: ['code-context', 'error-handling']
  },
  'error-handling': {
    id: 'error-handling',
    title: '错误处理最佳实践',
    description: '编写健壮的错误处理代码',
    content: `
良好的错误处理是专业代码的标志。

**错误处理原则：**
1. 预见可能的错误
2. 提供有用的错误信息
3. 优雅降级，不让应用崩溃
4. 记录错误日志
5. 给用户友好的提示

**常见错误类型：**
- 网络错误：请求失败、超时
- 数据错误：null/undefined、类型不匹配
- 权限错误：未授权、token过期
- 业务错误：余额不足、库存不够

**TypeScript示例：**
\`\`\`typescript
try {
  const result = await fetchUserData(userId);
  return result;
} catch (error) {
  if (error instanceof NetworkError) {
    // 网络错误：重试或提示离线
    logger.error('Network failed', error);
    return { error: '网络连接失败，请检查网络' };
  } else if (error instanceof AuthError) {
    // 认证错误：跳转登录
    redirectToLogin();
  } else {
    // 未知错误：上报并提示通用错误
    reportError(error);
    return { error: '操作失败，请稍后重试' };
  }
}
\`\`\`

**AI可以帮你：**
- 识别代码中缺少的错误处理
- 提供针对特定场景的错误处理方案
- 改进错误消息的可读性
- 添加日志和监控点
    `.trim(),
    relatedConcepts: ['debugging-with-ai', 'code-review']
  },
  'code-review': {
    id: 'code-review',
    title: 'AI代码审查',
    description: '利用AI提升代码质量',
    content: `
AI可以作为你的代码审查伙伴，提供即时反馈。

**审查维度：**
1. 功能正确性：逻辑是否正确
2. 性能优化：是否有性能瓶颈
3. 代码风格：是否符合最佳实践
4. 安全性：是否有安全漏洞
5. 可维护性：是否易于理解和修改

**审查提示词示例：**
\`\`\`
请审查以下代码，关注：
1. 潜在的bug和边界情况
2. 性能优化机会
3. 代码可读性和可维护性
4. 是否符合SOLID原则

[你的代码]
\`\`\`

**AI审查的优势：**
- 即时反馈，无需等待人工审查
- 发现常见错误和反模式
- 提供重构建议
- 学习最佳实践
- 24/7可用

**配合人工审查：**
- AI审查：快速发现明显问题
- 人工审查：关注业务逻辑和架构
- 组合使用效果最佳

**审查清单：**
- [ ] 变量命名是否清晰
- [ ] 函数是否单一职责
- [ ] 是否有重复代码
- [ ] 错误处理是否完善
- [ ] 是否添加必要的注释
- [ ] 边界情况是否考虑
- [ ] 性能是否可接受
    `.trim(),
    relatedConcepts: ['error-handling', 'refactoring']
  },
  'refactoring': {
    id: 'refactoring',
    title: '代码重构技巧',
    description: '在AI帮助下安全地改进代码',
    content: `
重构是在不改变外部行为的前提下改进代码结构。

**重构时机：**
- 代码重复出现3次以上
- 函数超过50行
- 需要添加新功能但代码难以扩展
- 代码难以理解和测试

**常见重构模式：**

1. **提取函数**
\`\`\`typescript
// 重构前
function processOrder(order) {
  // 验证订单
  if (!order.items || order.items.length === 0) {
    throw new Error('订单为空');
  }
  // 计算总价
  let total = 0;
  for (let item of order.items) {
    total += item.price * item.quantity;
  }
  // 应用折扣
  if (order.discount) {
    total *= (1 - order.discount);
  }
  return total;
}

// 重构后
function processOrder(order) {
  validateOrder(order);
  const subtotal = calculateSubtotal(order.items);
  return applyDiscount(subtotal, order.discount);
}
\`\`\`

2. **引入参数对象**
3. **拆分大类**
4. **移除重复代码**

**AI辅助重构：**
- 询问："这段代码可以如何重构？"
- 要求："将这个函数拆分成多个小函数"
- 请求："这段代码有什么设计模式可以应用？"

**重构原则：**
1. 一次只做一个改动
2. 每次改动后运行测试
3. 提交前确保功能不变
4. 保留原有的公共API
    `.trim(),
    relatedConcepts: ['code-review', 'testing-strategies']
  },
  'testing-strategies': {
    id: 'testing-strategies',
    title: '测试策略与实践',
    description: '编写有效的测试代码',
    content: `
测试是保证代码质量的关键。

**测试金字塔：**
\`\`\`
      /\\
     /E2E\\     <- 少量端到端测试
    /------\\
   /集成测试\\   <- 适量集成测试
  /----------\\
 /  单元测试  \\  <- 大量单元测试
/--------------\\
\`\`\`

**单元测试原则：**
- Fast：快速执行
- Independent：测试独立
- Repeatable：结果可重复
- Self-validating：自动验证
- Timely：及时编写

**AI帮你写测试：**
\`\`\`
提示词：为这个函数编写单元测试，包括：
1. 正常情况
2. 边界情况（空值、零、负数）
3. 异常情况（错误处理）

[你的函数代码]
\`\`\`

**测试驱动开发(TDD)：**
1. 红：写一个失败的测试
2. 绿：写最少代码让测试通过
3. 重构：改进代码质量

**Mock和Stub：**
- Mock：模拟外部依赖（API、数据库）
- Stub：提供预定义的响应
- AI可以帮你生成mock数据

**测试覆盖率：**
- 目标：80%以上
- 关注核心业务逻辑
- 不追求100%（性价比低）
    `.trim(),
    relatedConcepts: ['refactoring', 'debugging-with-ai']
  },
  'few-shot-learning': {
    id: 'few-shot-learning',
    title: 'Few-Shot Learning（少样本学习）',
    description: '通过示例引导AI理解你的需求',
    content: `
Few-Shot Learning是给AI提供几个示例，让它理解你想要的输出格式。

**Zero-Shot（零样本）：**
\`\`\`
提取这段文本的关键信息：
"白茆AI提供Claude和GLM-5两个产品线..."
\`\`\`

**One-Shot（单样本）：**
\`\`\`
将用户评论转换为结构化数据：

示例：
输入："这个产品很好用，但价格有点贵"
输出：{"sentiment": "positive", "aspects": {"quality": "好用", "price": "贵"}}

现在处理：
"界面设计很漂亮，就是加载速度慢"
\`\`\`

**Few-Shot（多样本）：**
\`\`\`
提取代码的关键信息：

示例1：
代码：function add(a, b) { return a + b; }
提取：{"name": "add", "params": ["a", "b"], "returns": "sum"}

示例2：
代码：async function fetchUser(id) { return await api.get(\`/users/\${id}\`); }
提取：{"name": "fetchUser", "params": ["id"], "returns": "user", "async": true}

现在处理这段代码：
[你的代码]
\`\`\`

**最佳实践：**
1. 示例要代表性强
2. 覆盖不同情况
3. 格式一致
4. 3-5个示例效果最好
    `.trim(),
    relatedConcepts: ['prompt-engineering']
  }
};

// 学习路径配置
export const LEARNING_PATHS: LearningPath[] = [
  {
    id: 'basics',
    title: 'AI编程基础',
    description: '掌握AI辅助编程的核心概念和基本技能',
    icon: '📚',
    color: 'blue',
    order: 1,
    tasks: [
      {
        id: 'task-1-1',
        title: '理解Prompt Engineering',
        description: '学习如何编写高质量的AI提示词',
        type: 'concept',
        difficulty: 'beginner',
        status: 'available',
        estimatedTime: 30,
        xpReward: 100,
        concepts: ['prompt-engineering'],
        content: {
          objectives: [
            '理解什么是Prompt Engineering',
            '掌握编写清晰提示词的技巧',
            '学会避免常见的提示词错误'
          ],
          keyPoints: [
            '清晰明确地表达需求',
            '提供充分的上下文信息',
            '指定期望的输出格式',
            '通过示例引导AI理解',
            '迭代优化提示词'
          ],
          resources: [
            {
              title: 'Prompt Engineering指南',
              url: '/docs#prompt-guide',
              type: 'doc'
            }
          ],
          exercises: [
            {
              question: '将"写个登录功能"改写成一个清晰的提示词',
              answer: '用React和TypeScript编写一个用户登录组件，包括：\n1. 邮箱和密码输入框\n2. 表单验证（邮箱格式、密码长度）\n3. 提交按钮和加载状态\n4. 错误提示显示\n5. 使用React Hook Form处理表单',
              hints: ['明确技术栈', '列出具体功能点', '说明实现细节']
            }
          ]
        }
      },
      {
        id: 'task-1-2',
        title: '认识Context Window',
        description: '了解AI模型的记忆限制',
        type: 'concept',
        difficulty: 'beginner',
        status: 'available',
        estimatedTime: 20,
        xpReward: 80,
        concepts: ['context-window'],
        prerequisites: ['task-1-1'],
        content: {
          objectives: [
            '理解什么是Context Window',
            '知道不同模型的上下文限制',
            '学会在长对话中管理上下文'
          ],
          keyPoints: [
            'Context Window是token数量限制',
            'Claude Sonnet、Oups: 1M',
            'GLM-5: 1M',
            '超出限制的内容会被遗忘',
            '重要信息放在开头和结尾'
          ]
        }
      },
      {
        id: 'task-1-3',
        title: 'Token使用优化',
        description: '学习如何高效使用token，降低成本',
        type: 'concept',
        difficulty: 'beginner',
        status: 'available',
        estimatedTime: 25,
        xpReward: 90,
        concepts: ['token-usage'],
        prerequisites: ['task-1-2'],
        content: {
          objectives: [
            '理解token的计费规则',
            '掌握token优化技巧',
            '学会选择合适的模型'
          ],
          keyPoints: [
            '1 token ≈ 0.75个英文单词',
            '输入和输出都计费',
            '精简提示词，去除冗余',
            '简单任务用便宜模型',
            '复杂任务用强大模型'
          ],
          exercises: [
            {
              question: '如何优化这个提示词来减少token使用："我有一个React项目，使用了TypeScript，现在遇到一个问题，就是当我点击按钮的时候，状态没有更新，我不知道为什么..."（120字）',
              answer: 'React+TS项目，点击按钮后状态未更新，请帮忙排查原因。\n[粘贴相关代码]\n\n（从120字减少到约30字，节省约75%的token）',
              hints: ['去除冗余表达', '直接说明问题', '用代码代替描述']
            }
          ]
        }
      },
      {
        id: 'task-1-4',
        title: '实践：第一个AI辅助编程任务',
        description: '使用AI编写一个实用的小功能',
        type: 'practice',
        difficulty: 'beginner',
        status: 'available',
        estimatedTime: 45,
        xpReward: 150,
        concepts: ['prompt-engineering', 'token-usage'],
        prerequisites: ['task-1-3'],
        content: {
          objectives: [
            '综合运用所学知识',
            '完成一个实际编程任务',
            '体验AI辅助开发的完整流程'
          ],
          keyPoints: [
            '选择合适的任务',
            '编写清晰的提示词',
            '审查AI生成的代码',
            '测试和调试',
            '总结经验'
          ],
          exercises: [
            {
              question: '任务：使用AI帮助你编写一个"邮箱验证"函数（TypeScript）',
              answer: `提示词示例：
"用TypeScript编写一个邮箱验证函数：
1. 函数名：isValidEmail
2. 参数：email字符串
3. 返回：boolean
4. 验证规则：标准RFC 5322格式
5. 包含单元测试用例
6. 添加JSDoc注释"`,
              hints: ['应用Prompt Engineering原则', '明确所有需求', '要求包含测试']
            }
          ]
        }
      }
    ]
  },
  {
    id: 'practical',
    title: '实战技能',
    description: '学习日常开发中的AI应用技巧',
    icon: '🛠️',
    color: 'green',
    order: 2,
    requiredXP: 300,
    tasks: [
      {
        id: 'task-2-1',
        title: '代码上下文管理',
        description: '学会向AI提供有效的代码上下文',
        type: 'concept',
        difficulty: 'intermediate',
        status: 'locked',
        estimatedTime: 35,
        xpReward: 120,
        concepts: ['code-context'],
        content: {
          objectives: [
            '理解什么是代码上下文',
            '学会组织上下文信息',
            '掌握上下文提供的技巧'
          ],
          keyPoints: [
            '提供技术栈和版本信息',
            '包含相关代码片段',
            '附带错误信息和日志',
            '说明预期和实际行为',
            '提及已尝试的方案'
          ]
        }
      },
      {
        id: 'task-2-2',
        title: 'AI辅助调试',
        description: '高效利用AI解决bug',
        type: 'practice',
        difficulty: 'intermediate',
        status: 'locked',
        estimatedTime: 40,
        xpReward: 130,
        concepts: ['debugging-with-ai', 'code-context'],
        prerequisites: ['task-2-1'],
        content: {
          objectives: [
            '掌握AI调试的正确流程',
            '学会编写有效的调试提示词',
            '理解如何分析AI的建议'
          ],
          keyPoints: [
            '提供最小可复现代码',
            '完整的错误堆栈',
            '明确期望和实际行为',
            '说明运行环境',
            '验证AI的解决方案'
          ]
        }
      },
      {
        id: 'task-2-3',
        title: '错误处理最佳实践',
        description: '编写健壮的错误处理代码',
        type: 'concept',
        difficulty: 'intermediate',
        status: 'locked',
        estimatedTime: 30,
        xpReward: 110,
        concepts: ['error-handling'],
        prerequisites: ['task-2-2'],
        content: {
          objectives: [
            '理解错误处理的重要性',
            '学习常见错误类型',
            '掌握错误处理模式'
          ],
          keyPoints: [
            '预见可能的错误',
            '提供有用的错误信息',
            '优雅降级',
            '记录错误日志',
            '用户友好的提示'
          ]
        }
      },
      {
        id: 'task-2-4',
        title: 'AI代码审查',
        description: '利用AI提升代码质量',
        type: 'practice',
        difficulty: 'intermediate',
        status: 'locked',
        estimatedTime: 50,
        xpReward: 160,
        concepts: ['code-review', 'error-handling'],
        prerequisites: ['task-2-3'],
        content: {
          objectives: [
            '学会使用AI进行代码审查',
            '理解审查的关键维度',
            '掌握改进代码的方法'
          ],
          keyPoints: [
            '功能正确性检查',
            '性能优化建议',
            '代码风格改进',
            '安全漏洞识别',
            '可维护性提升'
          ]
        }
      }
    ]
  },
  {
    id: 'advanced',
    title: '进阶技术',
    description: '掌握高级AI编程技术',
    icon: '🚀',
    color: 'purple',
    order: 3,
    requiredXP: 700,
    tasks: [
      {
        id: 'task-3-1',
        title: '代码重构技巧',
        description: '在AI帮助下安全重构代码',
        type: 'concept',
        difficulty: 'advanced',
        status: 'locked',
        estimatedTime: 45,
        xpReward: 180,
        concepts: ['refactoring'],
        content: {
          objectives: [
            '理解重构的时机和原则',
            '学习常见重构模式',
            '掌握AI辅助重构技巧'
          ],
          keyPoints: [
            '识别代码异味',
            '应用重构模式',
            '保持功能不变',
            '逐步小步改进',
            '配合测试验证'
          ]
        }
      },
      {
        id: 'task-3-2',
        title: '测试策略与实践',
        description: '编写有效的测试代码',
        type: 'practice',
        difficulty: 'advanced',
        status: 'locked',
        estimatedTime: 60,
        xpReward: 200,
        concepts: ['testing-strategies'],
        prerequisites: ['task-3-1'],
        content: {
          objectives: [
            '理解测试金字塔',
            '掌握单元测试原则',
            '学会使用AI生成测试'
          ],
          keyPoints: [
            'TDD测试驱动开发',
            '单元测试编写',
            'Mock和Stub使用',
            '测试覆盖率',
            'AI生成测试用例'
          ]
        }
      },
      {
        id: 'task-3-3',
        title: 'Few-Shot Learning技巧',
        description: '通过示例精准引导AI',
        type: 'concept',
        difficulty: 'advanced',
        status: 'locked',
        estimatedTime: 40,
        xpReward: 170,
        concepts: ['few-shot-learning', 'prompt-engineering'],
        prerequisites: ['task-3-2'],
        content: {
          objectives: [
            '理解Few-Shot Learning',
            '掌握示例编写技巧',
            '提高AI输出准确性'
          ],
          keyPoints: [
            'Zero-Shot vs Few-Shot',
            '选择代表性示例',
            '保持格式一致',
            '3-5个示例最佳',
            '覆盖不同情况'
          ]
        }
      },
      {
        id: 'task-3-4',
        title: '综合项目实战',
        description: '使用AI完成一个完整的小项目',
        type: 'project',
        difficulty: 'advanced',
        status: 'locked',
        estimatedTime: 120,
        xpReward: 300,
        concepts: ['prompt-engineering', 'code-review', 'testing-strategies', 'refactoring'],
        prerequisites: ['task-3-3'],
        content: {
          objectives: [
            '综合运用所学技能',
            '体验完整开发流程',
            '形成AI协作工作流'
          ],
          keyPoints: [
            '需求分析',
            '架构设计',
            '迭代开发',
            '代码审查',
            '测试验证',
            '优化重构'
          ],
          exercises: [
            {
              question: '项目：开发一个"待办事项"应用（Todo App）\n要求：\n- 技术栈：React + TypeScript\n- 功能：增删改查、过滤、持久化\n- 代码质量：测试覆盖80%以上\n- 全程使用AI辅助开发',
              answer: '通过AI辅助完成：\n1. 技术架构设计\n2. 组件结构规划\n3. 状态管理实现\n4. 业务逻辑编写\n5. 测试用例生成\n6. 代码审查优化\n\n项目完成后总结AI使用经验',
              hints: ['分阶段推进', '每阶段都用AI辅助', '记录有用的提示词', '总结经验教训']
            }
          ]
        }
      }
    ]
  },
  {
    id: 'expert',
    title: '专家之路',
    description: '成为AI辅助编程专家',
    icon: '👑',
    color: 'yellow',
    order: 4,
    requiredXP: 1500,
    tasks: [
      {
        id: 'task-4-1',
        title: '模型选择策略',
        description: '为不同任务选择最优模型',
        type: 'concept',
        difficulty: 'expert',
        status: 'locked',
        estimatedTime: 35,
        xpReward: 150,
        concepts: ['model-selection'],
        content: {
          objectives: [
            '理解不同模型的特点',
            '掌握模型选择决策树',
            '优化成本和效果平衡'
          ],
          keyPoints: [
            'Claude vs GLM-5特点',
            '按任务类型选模型',
            '按预算选方案',
            '双模型验证',
            'Mini适合学习，Max适合生产'
          ]
        }
      },
      {
        id: 'task-4-2',
        title: '构建AI工作流',
        description: '设计高效的AI协作流程',
        type: 'practice',
        difficulty: 'expert',
        status: 'locked',
        estimatedTime: 60,
        xpReward: 220,
        concepts: ['prompt-engineering', 'model-selection', 'code-review'],
        prerequisites: ['task-4-1'],
        content: {
          objectives: [
            '设计个性化工作流',
            '建立提示词库',
            '优化协作效率'
          ],
          keyPoints: [
            '需求分析阶段',
            '设计架构阶段',
            '编码实现阶段',
            '测试优化阶段',
            '文档编写阶段'
          ]
        }
      },
      {
        id: 'task-4-3',
        title: '团队AI实践',
        description: '在团队中推广AI最佳实践',
        type: 'concept',
        difficulty: 'expert',
        status: 'locked',
        estimatedTime: 45,
        xpReward: 190,
        concepts: ['code-review', 'testing-strategies'],
        prerequisites: ['task-4-2'],
        content: {
          objectives: [
            '制定团队AI使用规范',
            '建立知识共享机制',
            '培训团队成员'
          ],
          keyPoints: [
            '提示词模板库',
            '代码审查流程',
            '质量标准',
            '经验分享会',
            '持续改进'
          ]
        }
      },
      {
        id: 'task-4-4',
        title: '大师挑战：开源项目贡献',
        description: '使用AI为开源项目做贡献',
        type: 'project',
        difficulty: 'expert',
        status: 'locked',
        estimatedTime: 180,
        xpReward: 500,
        concepts: Object.keys(CONCEPTS),
        prerequisites: ['task-4-3'],
        content: {
          objectives: [
            '选择合适的开源项目',
            '理解项目代码库',
            '贡献高质量PR',
            '与社区互动'
          ],
          keyPoints: [
            '选择感兴趣的项目',
            'AI辅助代码理解',
            '修复issue或添加功能',
            '编写测试和文档',
            '提交PR并响应review',
            '总结并分享经验'
          ],
          exercises: [
            {
              question: '挑战：为一个GitHub开源项目贡献代码\n1. 选择一个你感兴趣的项目\n2. 使用AI理解代码库\n3. 找到issue或提出改进\n4. 编写代码和测试\n5. 提交PR并完成merge',
              answer: '完成后获得：\n- 开源贡献经验\n- 代码审查能力\n- AI辅助编程专家认证',
              hints: ['从小issue开始', '详细阅读贡献指南', '充分测试代码', '礼貌回应reviewer']
            }
          ]
        }
      }
    ]
  }
];

// 计算用户等级（基于经验值）
export function calculateLevel(xp: number): number {
  // 等级公式：level = floor(sqrt(xp / 100))
  return Math.floor(Math.sqrt(xp / 100)) + 1;
}

// 计算下一等级所需经验值
export function getNextLevelXP(currentLevel: number): number {
  return (currentLevel ** 2) * 100;
}

// 获取用户当前可访问的学习路径
export function getAccessiblePaths(userXP: number): LearningPath[] {
  return LEARNING_PATHS.filter(path =>
    !path.requiredXP || userXP >= path.requiredXP
  );
}

// 更新任务状态（基于前置任务完成情况）
export function updateTaskStatus(
  path: LearningPath,
  completedTaskIds: string[]
): LearningPath {
  return {
    ...path,
    tasks: path.tasks.map(task => {
      // 如果已完成，状态为completed
      if (completedTaskIds.includes(task.id)) {
        return { ...task, status: 'completed' };
      }

      // 检查前置任务是否都已完成
      const prerequisitesMet = !task.prerequisites ||
        task.prerequisites.every(preId => completedTaskIds.includes(preId));

      // 如果前置任务完成，状态为available，否则为locked
      return {
        ...task,
        status: prerequisitesMet ? 'available' : 'locked'
      };
    })
  };
}
